import { useState } from 'react';

import { ServingGeo } from '../../../../../../backend/src/llm/serving-geo';
import { useCreateLlmPrice } from '@/api/mutations/useCreateLlmPrice';
import { EditFieldDialogControlled } from '@/new/components/edit-field-dialog';
import { Icon } from '@/new/components/icon';
import { Button } from '@/new/components/shadcn/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/new/components/shadcn/dialog';
import { Input } from '@/new/components/shadcn/input';
import { Label } from '@/new/components/shadcn/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
} from '@/new/components/shadcn/select';

const NEW_MODEL_VALUE = '__new-model__';

const RATE_FIELDS = [
  { key: 'inputUsdPerMTok', label: 'Input', hint: 'Uncached input tokens' },
  { key: 'cacheReadUsdPerMTok', label: 'Cache read', hint: 'Tokens served from the prompt cache' },
  { key: 'cacheWriteUsdPerMTok', label: 'Cache write', hint: 'Tokens written to the prompt cache' },
  { key: 'outputUsdPerMTok', label: 'Output', hint: 'Generated tokens' },
] as const;

type RateKey = (typeof RATE_FIELDS)[number]['key'];

const emptyRates: Record<RateKey, string> = {
  inputUsdPerMTok: '',
  cacheReadUsdPerMTok: '',
  cacheWriteUsdPerMTok: '',
  outputUsdPerMTok: '',
};

/**
 * Appends a price version.
 *
 * Model and geo use the shared selects. A model can also be entered from the
 * final model option before its first price version is created.
 */
export function NewPriceVersionDialog({ knownModels }: { knownModels: string[] }) {
  const [isOpen, setIsOpen] = useState(false);
  const [isNewModelOpen, setIsNewModelOpen] = useState(false);
  const [model, setModel] = useState('');
  const [addedModels, setAddedModels] = useState<string[]>([]);
  const [servingGeo, setServingGeo] = useState<ServingGeo | ''>('');
  const [rates, setRates] = useState(emptyRates);

  const modelOptions = [...new Set([...knownModels, ...addedModels])].sort();

  const { mutate: createLlmPrice, isLoading } = useCreateLlmPrice();

  const isComplete =
    model.trim() !== '' &&
    servingGeo !== '' &&
    // Strictly positive, matching the backend DTO and kim-agent's ModelPrice.
    RATE_FIELDS.every(({ key }) => rates[key] !== '' && Number.isFinite(Number(rates[key])) && Number(rates[key]) > 0);

  const reset = () => {
    setModel('');
    setAddedModels([]);
    setServingGeo('');
    setRates(emptyRates);
  };

  const handleSubmit = () => {
    if (!isComplete) return;

    createLlmPrice(
      {
        model: model.trim(),
        servingGeo,
        inputUsdPerMTok: Number(rates.inputUsdPerMTok),
        cacheReadUsdPerMTok: Number(rates.cacheReadUsdPerMTok),
        cacheWriteUsdPerMTok: Number(rates.cacheWriteUsdPerMTok),
        outputUsdPerMTok: Number(rates.outputUsdPerMTok),
      },
      {
        onSuccess: () => {
          reset();
          setIsOpen(false);
        },
      },
    );
  };

  return (
    <>
      <Dialog
        open={isOpen}
        onOpenChange={(open) => {
          setIsOpen(open);
          if (!open) reset();
        }}
      >
        <DialogTrigger asChild>
          <Button>
            <Icon className="lucide--plus" />
            New price version
          </Button>
        </DialogTrigger>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>New price version</DialogTitle>
            <DialogDescription>
              Adds a version rather than editing an existing one, effective immediately. Rates are USD per million
              tokens, and must match the AWS Bedrock rate for the inference profile Kim actually calls.
            </DialogDescription>
          </DialogHeader>

          <div className="mt-4 grid gap-4">
            <div className="grid gap-1.5">
              <div className="px-1">
                <Label htmlFor="price-model">Model</Label>
                <p className="text-gray-500">Base model id, with no geo prefix.</p>
              </div>
              <Select
                value={model}
                onValueChange={(value) => {
                  if (value === NEW_MODEL_VALUE) setIsNewModelOpen(true);
                  else setModel(value);
                }}
              >
                <SelectTrigger id="price-model">
                  <SelectValue placeholder="Select a model" />
                </SelectTrigger>
                <SelectContent>
                  {modelOptions.map((known) => (
                    <SelectItem key={known} value={known} className="font-mono">
                      {known}
                    </SelectItem>
                  ))}
                  {modelOptions.length > 0 && <SelectSeparator />}
                  <SelectItem value={NEW_MODEL_VALUE}>
                    <span className="flex items-center gap-1.5">
                      <Icon className="lucide--plus" /> New model…
                    </span>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="grid gap-1.5">
              <div className="px-1">
                <Label htmlFor="price-servingGeo">Geo</Label>
                <p className="text-gray-500">
                  Bedrock cross-region inference geo, not an AWS region: a profile costs the same across every region it
                  spans.
                </p>
              </div>
              <Select value={servingGeo} onValueChange={(value) => setServingGeo(value as ServingGeo)}>
                <SelectTrigger id="price-servingGeo">
                  <SelectValue placeholder="Select a geo" />
                </SelectTrigger>
                <SelectContent>
                  {Object.values(ServingGeo).map((geo) => (
                    <SelectItem key={geo} value={geo}>
                      {geo}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              {RATE_FIELDS.map((field) => (
                <div key={field.key} className="grid gap-1.5">
                  <div className="px-1">
                    <Label htmlFor={`price-${field.key}`}>{field.label}</Label>
                    <p className="text-sm text-gray-500">{field.hint}</p>
                  </div>
                  <Input
                    id={`price-${field.key}`}
                    type="number"
                    min="0.000001"
                    step="0.000001"
                    value={rates[field.key]}
                    onChange={(event) => setRates({ ...rates, [field.key]: event.target.value })}
                    placeholder="0.000000"
                  />
                </div>
              ))}
            </div>
          </div>

          <DialogFooter className="mt-6">
            <Button variant="outline" onClick={() => setIsOpen(false)} disabled={isLoading}>
              Cancel
            </Button>
            <Button onClick={handleSubmit} disabled={!isComplete || isLoading}>
              {isLoading ? 'Adding…' : 'Add version'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <EditFieldDialogControlled
        open={isNewModelOpen}
        onOpenChange={setIsNewModelOpen}
        title="Add model"
        description="Enter the base model id, with no geo prefix."
        fieldLabel="Model"
        currentValue=""
        placeholder="anthropic.claude-opus-4-8"
        saveLabel="Add model"
        onSave={(value) => {
          const newModel = value.trim();
          if (!newModel) throw new Error('A model id is required');
          setAddedModels((current) => [...new Set([...current, newModel])]);
          setModel(newModel);
        }}
      />
    </>
  );
}
